import {
  ChevronDown,
  CreditCard,
  ExternalLink,
  Handshake,
  Headset,
  LogOut,
  Megaphone,
  MoonStar,
  Sun,
  UserRound,
  UserSearch,
} from 'lucide-react'
import { Link, NavLink, useNavigate } from 'react-router-dom'

import { OrganizationSwitcher } from '@/app/layout/organization-switcher'
import { ProjectSwitcher } from '@/app/layout/project-switcher'
import { useAuth } from '@/features/auth/auth-context'
import type { BillingSummary } from '@/features/billing/billing-summary'
import { formatRenewalDate } from '@/features/billing/billing-summary'
import { BillingSummaryChip } from '@/features/billing/components/billing-summary-chip'
import { useBillingSummary } from '@/features/billing/use-billing-summary'
import { NotificationBell } from '@/features/notifications/notification-bell'
import { useOperatorQueueCount } from '@/features/projects/use-operator-queue-count'
import { useUnreadConversations } from '@/features/projects/use-unread-conversations'
import { BrandLogo } from '@/shared/components/common/brand-logo'
import { CountBadge } from '@/shared/components/ui/count-badge'
import { DropdownItem, DropdownMenu } from '@/shared/components/ui/dropdown-menu'
import { useTheme } from '@/shared/providers/theme-provider'
import { cn } from '@/shared/utils/cn'

const topNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-bg)]',
    isActive
      ? 'bg-[var(--color-accent)]/12 text-[var(--color-text)]'
      : 'text-[var(--color-text-soft)] hover:bg-[var(--panel-subtle)] hover:text-[var(--color-text)]',
  )

// Mobile dropdown balance line — same tone semantics as the desktop chip.
const balanceToneClass: Record<BillingSummary['tone'], string> = {
  danger: 'text-[var(--color-danger-solid)]',
  warning: 'text-[var(--color-warning-solid)]',
  ok: 'text-[var(--color-text-soft)]',
}

export function Topbar() {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()

  const queueCount = useOperatorQueueCount()
  const { leadsTotal } = useUnreadConversations()
  const billing = useBillingSummary()

  return (
    <header className="sticky top-0 z-20 border-b border-[var(--panel-border)] bg-[var(--panel)]/85 px-4 py-2.5 backdrop-blur sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            className="flex items-center gap-2 font-semibold tracking-tight text-[var(--color-text)]"
            to="/projects"
          >
            <BrandLogo className="h-7 w-7 flex-none" />
            <span className="hidden sm:inline">
              ИИ-<span className="font-bold">Менеджер</span>
            </span>
          </Link>
          <ProjectSwitcher />
          <OrganizationSwitcher />
        </div>

        <div className="flex items-center gap-1.5">
          <nav className="hidden items-center gap-1 md:flex">
            <NavLink className={topNavLinkClass} to="/operator">
              <Headset className="h-4 w-4" />
              Очередь
              <CountBadge count={queueCount} />
            </NavLink>
            <NavLink className={topNavLinkClass} to="/leads">
              <UserSearch className="h-4 w-4" />
              Лиды
              <CountBadge count={leadsTotal} />
            </NavLink>
            <NavLink className={topNavLinkClass} to="/billing">
              <CreditCard className="h-4 w-4" />
              Счёт
            </NavLink>
            <NavLink className={topNavLinkClass} to="/avito">
              <Megaphone className="h-4 w-4" />
              Avito
            </NavLink>
            <NavLink className={topNavLinkClass} to="/partner-program">
              <Handshake className="h-4 w-4" />
              Партнёрская программа
            </NavLink>
            <NavLink className={topNavLinkClass} to="/account">
              <UserRound className="h-4 w-4" />
              Аккаунт
            </NavLink>
          </nav>

          <BillingSummaryChip summary={billing} />

          <NotificationBell />

          <DropdownMenu
            trigger={
              <span className="flex items-center gap-1 rounded-lg px-1.5 py-1 hover:bg-[var(--panel-subtle)]">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-accent)]/15 text-sm font-semibold text-[var(--color-accent)]">
                  {(user?.name || 'U').slice(0, 1).toUpperCase()}
                </span>
                <ChevronDown className="h-4 w-4 text-[var(--color-text-muted)]" />
              </span>
            }
          >
            {(close) => (
              <div className="min-w-[220px]">
                <div className="border-b border-[var(--panel-border)] px-2 py-2">
                  <p className="truncate text-sm font-semibold">{user?.name || 'User'}</p>
                  <p className="truncate text-xs text-[var(--color-text-muted)]">{user?.email}</p>
                  <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                    Счёт:{' '}
                    <span className={cn('font-medium', balanceToneClass[billing.tone])}>
                      {billing.hasBalance ? billing.tokenBalance.toLocaleString('ru-RU') : '—'}{' '}
                      токенов
                    </span>
                    {billing.renewalDate
                      ? ` · ${billing.ended ? 'истекла' : 'до'} ${formatRenewalDate(billing.renewalDate)}`
                      : ''}
                  </p>
                </div>
                <div className="md:hidden">
                  <DropdownItem
                    icon={<Headset className="h-4 w-4" />}
                    onClick={() => {
                      close()
                      navigate('/operator')
                    }}
                  >
                    Очередь
                  </DropdownItem>
                  <DropdownItem
                    icon={<UserSearch className="h-4 w-4" />}
                    onClick={() => {
                      close()
                      navigate('/leads')
                    }}
                  >
                    Лиды
                  </DropdownItem>
                  <DropdownItem
                    icon={<CreditCard className="h-4 w-4" />}
                    onClick={() => {
                      close()
                      navigate('/billing')
                    }}
                  >
                    Счёт
                  </DropdownItem>
                  <DropdownItem
                    icon={<Megaphone className="h-4 w-4" />}
                    onClick={() => {
                      close()
                      navigate('/avito')
                    }}
                  >
                    Avito
                  </DropdownItem>
                  <DropdownItem
                    icon={<Handshake className="h-4 w-4" />}
                    onClick={() => {
                      close()
                      navigate('/partner-program')
                    }}
                  >
                    Партнёрская программа
                  </DropdownItem>
                  <DropdownItem
                    icon={<UserRound className="h-4 w-4" />}
                    onClick={() => {
                      close()
                      navigate('/account')
                    }}
                  >
                    Аккаунт
                  </DropdownItem>
                </div>
                <DropdownItem
                  icon={
                    theme === 'dark' ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <MoonStar className="h-4 w-4" />
                    )
                  }
                  onClick={toggleTheme}
                >
                  Сменить тему
                </DropdownItem>
                {user?.is_super_admin === true ? (
                  <DropdownItem
                    icon={<ExternalLink className="h-4 w-4" />}
                    onClick={() =>
                      window.open('https://admin.ai-manager.website', '_blank', 'noopener')
                    }
                  >
                    Админ-панель
                  </DropdownItem>
                ) : null}
                <DropdownItem
                  destructive
                  icon={<LogOut className="h-4 w-4" />}
                  onClick={() => void logout()}
                >
                  Выйти
                </DropdownItem>
              </div>
            )}
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
